import {
  useCachedSelectedProject,
  useCachedVisibilityScore,
  useTranslation,
} from 'hooks';
import { useInfoCompletenessScore } from 'shared/hooks/useInfoCompletenessScore';
import { Skeleton } from 'shared/ui/skeleton/skeleton';
import { ScoreSquare } from 'shared/ui/score-square/score-square';
import { Card } from 'shared/ui/card/card';
import { getPositionColor } from 'components/mentions/mentions';
import cls from './impact-drawer-content.module.scss';

// TODO: Uncomment when backend implements GET /v1/informationcompleteness/products
// Expected response: { products: { [productName]: { completeness_score: number } } }
// Then add back the Completeness column in the products table
// const MOCK_PRODUCT_COMPLETENESS: Record<string, number> = {
//   'SEO Analytics Suite': 6.4,
//   'Competitor Analysis Platform': 5.1,
//   'Rank Tracking Software': 4.3,
// };

interface ProductImpact {
  name: string;
  aiVisibility: number; // mention_rate
  avgPosition: number; // mean_rank
  // TODO: Uncomment when backend data is available
  // completeness: number;
}

interface ImpactDrawerContentProps {
  showBusinessProfile?: boolean;
}

export const ImpactDrawerContent: React.FC<ImpactDrawerContentProps> = ({
  showBusinessProfile = true,
}) => {
  const { t } = useTranslation();
  const { visibilityScore, isLoading, isLoaded } = useCachedVisibilityScore();
  const { isTrialExpired, reportGenerating } = useCachedSelectedProject();
  const { infoCompletenessScore } = useInfoCompletenessScore();

  const productNames = Object.keys(visibilityScore?.products || {});

  const products: ProductImpact[] = productNames.map(name => {
    const product = visibilityScore?.products[name].entity;
    // TODO: Uncomment when backend data is available
    // const completeness = apiData[name]?.completeness_score ?? 0;
    return {
      name,
      aiVisibility: product?.mention_rate || 0,
      avgPosition: product?.mean_rank || 0,
      // TODO: Uncomment when backend data is available
      // completeness,
    };
  });

  const showSkeleton = reportGenerating ? !isLoaded : isLoading;
  const showLocked = isTrialExpired;

  return (
    <div className={cls.container}>
      {showBusinessProfile && (
        <Card className={cls.businessProfileCard}>
          <div className={cls.businessProfileContent}>
            <div className={cls.businessProfileInfo}>
              <h4 className={cls.businessProfileTitle}>
                {t('businessProfile')}
              </h4>
              <div className={cls.businessProfileScore}>
                <span className={cls.scoreLabel}>{t('infoCompleteness')}</span>
                {showSkeleton ? (
                  <Skeleton width={40} height={28} />
                ) : (
                  <ScoreSquare
                    score={Number(infoCompletenessScore.toFixed(1))}
                    isSmall
                    isLocked={showLocked}
                  />
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className={cls.productsCard}>
        <h4 className={cls.sectionTitle}>{t('productsAndServices')}</h4>
        {showSkeleton ? (
          <Skeleton
            height={20}
            width={200}
            className={cls.productsDescSkeleton}
          />
        ) : (
          <p className={cls.productsDesc}>
            {t('affectedProductsListedBelow', undefined, undefined, {
              count: products.length,
            })}
          </p>
        )}
        {showSkeleton ? (
          <>
            <Skeleton height={50} />
            <Skeleton height={50} />
            <Skeleton height={50} />
          </>
        ) : (
          <table className={cls.table}>
            <thead className={cls.tableHead}>
              <tr>
                <th className={cls.nameColumn}>{t('name')}</th>
                <th className={cls.scoreColumn}>{t('visibility')}</th>
                <th className={cls.scoreColumn}>{t('avgPosition')}</th>
                {/* TODO: Uncomment when backend data is available */}
                {/* <th className={cls.scoreColumn}>{t('completenessScore')}</th> */}
              </tr>
            </thead>
            <tbody>
              {products.map((product, idx) => (
                <tr key={idx}>
                  <td className={cls.productName}>{product.name}</td>
                  <td className={cls.scoreCell}>
                    <ScoreSquare
                      score={Number(product.aiVisibility.toFixed(1))}
                      isSmall
                      isLocked={showLocked}
                    />
                  </td>
                  <td className={cls.scoreCell}>
                    {showLocked ? (
                      <ScoreSquare score={0} isSmall isLocked direction='inv' />
                    ) : (
                      <span
                        className={`${cls.positionValue} ${getPositionColor(Math.round(product.avgPosition), 20)}`}
                      >
                        {product.avgPosition === 0 || product.avgPosition > 20
                          ? '>20'
                          : `${product.avgPosition.toFixed(1)}/20`}
                      </span>
                    )}
                  </td>
                  {/* TODO: Uncomment when backend data is available */}
                  {/* <td className={cls.scoreCell}>
                    <ScoreSquare
                      score={Number(product.completeness.toFixed(1))}
                      isSmall
                      isLocked={showLocked}
                    />
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
};
